import React, { useState, useEffect } from 'react';
import { subscribeRoom, setReady, startGame, leaveRoom } from '../shared/roomTx';
import { startChainOrderPhase } from '../shared/chainRoomTx';
import { FL_ROOM_STATUS, FIVE_LEAGUE, FL_GAME_MODE, CHAIN_PHASE } from '../shared/fiveLeague';
import { CyberBackground, NeonText, CyberButton, GlowingBorder } from '../components/CyberEffects';
import './Wait.css';

function Wait({ navigate, playerId, roomId }) {
  const [room, setRoom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [starting, setStarting] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(false);

  const copyToClipboard = async (text, setCopiedState) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedState(true);
      setTimeout(() => setCopiedState(false), 2000);
    } catch (e) {
      const input = document.createElement('input');
      input.value = text;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopiedState(true);
      setTimeout(() => setCopiedState(false), 2000);
    }
  };

  const handleCopyCode = () => copyToClipboard(room?.roomCode || '', setCopiedCode);

  const handleCopyTvUrl = () => {
    const url = `${window.location.origin}/#tv?roomId=${roomId}`;
    copyToClipboard(url, setCopiedUrl);
  };

  const handleShare = async () => {
    const joinUrl = `${window.location.origin}/#join?code=${room?.roomCode}`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'クイズ ワン ワードに参加',
          text: `ルームコード: ${room?.roomCode}`,
          url: joinUrl,
        });
      } catch (e) {
        // ユーザーがキャンセルした場合など
      }
    } else {
      copyToClipboard(joinUrl, setCopiedCode);
    }
  };

  // ルーム購読
  useEffect(() => {
    if (!roomId) {
      navigate('home');
      return;
    }

    const unsub = subscribeRoom(roomId, (data) => {
      setLoading(false);
      if (!data) {
        setError('ルームが見つかりません');
        return;
      }

      // このプレイヤーがルームに参加しているか確認
      const isPlayer = data.players?.some(p => p.playerId === playerId);
      if (!isPlayer) {
        setError('このルームに参加していません');
        return;
      }

      setRoom(data);

      // チェーンモードで順番決めフェーズになったらChainOrder画面へ
      if (data.game?.phase === CHAIN_PHASE.ORDER) {
        navigate('chain-order', { roomId });
        return;
      }

      // ゲーム開始したら適切な画面へ
      if (data.status === FL_ROOM_STATUS.PLAYING) {
        if (data.gameMode === FL_GAME_MODE.CHAIN) {
          navigate('chain-game', { roomId });
        } else {
          navigate('game', { roomId });
        }
      }
    });

    return () => unsub();
  }, [roomId, playerId, navigate]);

  const handleToggleReady = async () => {
    if (!room) return;
    const me = room.players?.find(p => p.playerId === playerId);
    if (!me) return;

    await setReady(roomId, playerId, !me.ready);
  };

  const handleStartGame = async () => {
    if (!room || starting) return;

    setStarting(true);
    setError('');

    // チェーンモードの場合は順番決めフェーズへ
    if (room.gameMode === FL_GAME_MODE.CHAIN) {
      const result = await startChainOrderPhase(roomId, playerId);
      if (!result.ok) {
        if (result.reason === 'not-enough-players') {
          setError(`最低${FIVE_LEAGUE.MIN_PLAYERS}人必要です`);
        } else if (result.reason === 'not-all-ready') {
          setError('全員が準備完了していません');
        } else {
          setError('ゲームを開始できませんでした');
        }
        setStarting(false);
      }
      // 成功時はsubscribeRoomがORDERフェーズを検知して全員遷移
      return;
    }

    const result = await startGame(roomId, playerId);
    if (!result.ok) {
      if (result.reason === 'not-enough-players') {
        setError(`最低${FIVE_LEAGUE.MIN_PLAYERS}人必要です`);
      } else if (result.reason === 'not-all-ready') {
        setError('全員が準備完了していません');
      } else {
        setError('ゲームを開始できませんでした');
      }
      setStarting(false);
    }
  };

  const handleLeave = async () => {
    await leaveRoom(roomId, playerId);
    navigate('home');
  };

  if (loading) {
    return (
      <div className="wait-container">
        <div className="loading-spinner">読み込み中...</div>
      </div>
    );
  }

  if (error && !room) {
    return (
      <div className="wait-container">
        <p className="error-message">{error}</p>
        <button className="btn-secondary" onClick={() => navigate('home')}>
          ホームに戻る
        </button>
      </div>
    );
  }

  if (!room) return null;

  const me = room.players?.find(p => p.playerId === playerId);
  const isHost = room.hostId === playerId;
  const controllers = room.players || [];
  const allReady = controllers.every(p => p.ready);
  const canStart = isHost && controllers.length >= FIVE_LEAGUE.MIN_PLAYERS && allReady;

  return (
    <div className="wait-container animate-fade-in">
      <CyberBackground showParticles={true} />

      <div className="wait-content">
        <div className="wait-header">
          <h1 className="wait-title">
            <NeonText color="accent" size="lg">待機中</NeonText>
          </h1>
          <GlowingBorder color="accent" className="room-code-display">
            <span className="room-code-label">ルームコード</span>
            <span className="room-code-value">{room.roomCode}</span>
            <div className="room-code-actions">
              <button className="code-action-btn" onClick={handleCopyCode}>
                {copiedCode ? '✓' : '📋'} コピー
              </button>
              <button className="code-action-btn" onClick={handleShare}>
                📤 共有
              </button>
            </div>
          </GlowingBorder>
          <div className="tv-link-section">
            <button className="copy-url-btn" onClick={handleCopyTvUrl}>
              {copiedUrl ? '✓ コピーしました' : '🖥️ TV用URLをコピー'}
            </button>
          </div>
        </div>

        <div className="players-section">
          <h2 className="section-title">
            <NeonText color="blue" size="sm">
              参加者 ({controllers.length}/{FIVE_LEAGUE.MAX_PLAYERS})
            </NeonText>
          </h2>
          <div className="players-list">
            {room.players?.map((player, idx) => (
              <GlowingBorder
                key={player.playerId}
                color={player.ready ? 'accent' : 'blue'}
                className={`player-card ${player.ready ? 'ready' : ''} ${player.playerId === playerId ? 'is-me' : ''}`}
              >
                <div className="player-info">
                  <span className="player-number">{idx + 1}</span>
                  <span className="player-name">
                    {player.nickname}
                    {player.playerId === room.hostId && <span className="host-badge">👑</span>}
                  </span>
                </div>
                <div className={`ready-badge ${player.ready ? 'active' : ''}`}>
                  {player.ready ? 'OK!' : '待機'}
                </div>
              </GlowingBorder>
            ))}

            {/* 空きスロット */}
            {Array.from({ length: FIVE_LEAGUE.MAX_PLAYERS - (room.players?.length || 0) }).map((_, i) => (
              <div key={`empty-${i}`} className="player-card empty">
                <span className="empty-slot">空き</span>
              </div>
            ))}
          </div>
        </div>

        {error && <p className="error-message">{error}</p>}

        <div className="wait-actions">
          {me && (
            me.ready ? (
              <CyberButton
                variant="accent"
                className="cancel-ready-btn"
                onClick={handleToggleReady}
              >
                やっぱり待って
              </CyberButton>
            ) : (
              <CyberButton
                variant="primary"
                className="ready-btn"
                onClick={handleToggleReady}
              >
                準備完了
              </CyberButton>
            )
          )}

          {isHost && (
            <CyberButton
              variant="accent"
              className="start-btn"
              onClick={handleStartGame}
              disabled={!canStart || starting}
            >
              {starting ? '開始中...' : 'ゲームスタート！'}
            </CyberButton>
          )}

          <button className="cyber-btn-secondary leave-btn" onClick={handleLeave}>
            退出する
          </button>
        </div>

        <div className="wait-info">
          <p>
            {controllers.length < FIVE_LEAGUE.MIN_PLAYERS
              ? `あと${FIVE_LEAGUE.MIN_PLAYERS - controllers.length}人で開始できます`
              : !allReady
              ? '全員が準備完了すると開始できます'
              : isHost
              ? '「ゲームスタート！」を押してください'
              : 'ホストの開始を待っています...'}
          </p>
        </div>
      </div>
    </div>
  );
}

export default Wait;
